import { ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";

const tdProducts = [
  {
    id: "double-eagle-2025",
    name: "1 Gram Double Eagle Goldnote Bar",
    subtitle: "2025 Edition",
    priceCAD: "C$256.08",
    priceUSD: "~$188 USD",
    availability: "In Stock",
    images: {
      obverse: "/1g-goldnote-obverse.png",
      reverse: "/1g-double-eagle-reverse-new.png"
    },
    description: "The iconic Double Eagle design on a 1 Gram Goldnote. Features 99.99% pure gold sealed within a polymer banknote, complete with secure QR authentication for instant verification.",
    features: [
      "1 Gram 99.99% Pure Gold",
      "Double Eagle Design",
      "QR Authentication",
      "TD Secure Storage Option",
      "Free Shipping on $500+ CAD"
    ],
    buyUrl: "https://preciousmetals.td.com/shop/en/tdmetals/golds/p1193"
  }
];

const ProductCard = ({ product }) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const isOutOfStock = product.availability === "Out of Stock";
  
  return (
    <div className="group bg-[#0A0A0A] border border-white/10 hover:border-[#D4AF37]/40 rounded-xl overflow-hidden transition-all duration-500 max-w-lg mx-auto">
      {/* Image with Flip */}
      <div 
        className="relative aspect-[4/3] p-8 flex items-center justify-center bg-gradient-to-b from-[#0A0A0A] to-[#050505] cursor-pointer"
        onMouseEnter={() => setIsFlipped(true)}
        onMouseLeave={() => setIsFlipped(false)}
        style={{ perspective: '1000px' }}
      >
        <div 
          className="w-full h-full transition-transform duration-700"
          style={{ 
            transformStyle: 'preserve-3d',
            transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
          }}
        >
          {/* Front - Obverse */}
          <div 
            className="absolute inset-0 flex items-center justify-center p-6"
            style={{ backfaceVisibility: 'hidden' }}
          >
            <img 
              src={product.images.obverse} 
              alt={`${product.name} - Obverse`}
              className="max-w-full max-h-full object-contain"
            />
          </div>
          {/* Back - Reverse */}
          <div 
            className="absolute inset-0 flex items-center justify-center p-6"
            style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
          >
            <img 
              src={product.images.reverse} 
              alt={`${product.name} - Reverse`}
              className="max-w-full max-h-full object-contain"
            />
          </div>
        </div>
        
        {/* Availability Badge */}
        <div className={`absolute top-4 right-4 px-3 py-1.5 rounded text-[0.6rem] tracking-wider uppercase font-medium z-10 ${
          isOutOfStock 
            ? 'bg-red-500/20 text-red-400 border border-red-500/30' 
            : 'bg-green-500/20 text-green-400 border border-green-500/30'
        }`}>
          {product.availability}
        </div>
        
        {/* Hover indicator */}
        <div className="absolute bottom-3 right-3 px-2 py-1 bg-black/70 text-[0.5rem] tracking-wider text-[#D4AF37]/70 uppercase rounded opacity-60 group-hover:opacity-0 transition-opacity z-10">
          Hover to flip
        </div>
      </div>
      
      {/* Content */}
      <div className="p-8">
        <p className="text-[0.6rem] tracking-[0.2em] uppercase text-[#D4AF37]/60 mb-2">{product.subtitle}</p>
        <h3 className="text-2xl text-white/90 mb-3" style={{ fontFamily: 'Cinzel, serif' }}>{product.name}</h3>
        
        {/* Pricing */}
        <div className="flex items-baseline gap-3 mb-4">
          <span className="text-3xl text-[#D4AF37] font-light">{product.priceCAD}</span>
          <span className="text-sm text-white/40">{product.priceUSD}</span>
        </div>
        
        <p className="text-sm text-white/70 font-light leading-relaxed mb-6">{product.description}</p>
        
        {/* Features */}
        <ul className="space-y-2 mb-8">
          {product.features.map((feature, index) => (
            <li key={index} className="flex items-center gap-2 text-sm text-white/70">
              <svg className="w-4 h-4 text-[#34A853]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {feature}
            </li>
          ))}
        </ul>
        
        {/* Buy Button */}
        <a 
          href={product.buyUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="block"
        >
          <Button 
            className={`w-full py-6 text-sm tracking-[0.15em] uppercase rounded-lg flex items-center justify-center gap-2 ${
              isOutOfStock 
                ? 'bg-white/10 text-white/70 cursor-not-allowed' 
                : 'bg-[#34A853] text-white hover:bg-[#2D9249]'
            }`}
            disabled={isOutOfStock}
          >
            Buy at TD Precious Metals
            <ExternalLink size={16} />
          </Button>
        </a>
      </div>
    </div>
  );
};

export const TDPreciousMetals = () => {
  return (
    <div className="min-h-screen bg-[#050505]" data-testid="td-page">
      {/* Hero Section */}
      <section className="relative pt-20 pb-12 md:pt-28 md:pb-16">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto text-center">
            {/* Retailer Badge */}
            <div className="inline-flex items-center gap-3 px-5 py-2 bg-[#0A0A0A] border border-[#34A853]/30 rounded-full mb-8">
              <img src="https://flagcdn.com/w40/ca.png" alt="Canada" className="w-6 h-4 object-cover rounded-sm" />
              <span className="text-[0.65rem] tracking-[0.3em] uppercase text-[#34A853]">Exclusive Canadian Retailer</span>
            </div>
            
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.1] mb-6">
              <span className="text-white/90">Available at</span>
              <br />
              <span className="text-[#34A853]">TD Precious Metals</span>
            </h1>
            <p className="text-base md:text-lg text-white/80 font-light leading-relaxed max-w-xl mx-auto mb-8">
              Purchase Goldnotes through TD Bank's trusted precious metals platform. 
              Secure storage options available for Canadian customers.
            </p>
            
            <a 
              href="https://preciousmetals.td.com/shop/en/tdmetals/golds"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#34A853] text-white hover:bg-[#2D9249] text-xs tracking-[0.2em] uppercase font-medium rounded-lg transition-colors"
            >
              Visit TD Precious Metals
              <ExternalLink size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="pb-24">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="mb-12">
            <p className="text-[0.625rem] tracking-[0.4em] uppercase text-[#D4AF37]/80 mb-4 text-center">
              Available Products
            </p>
            <h2 className="text-2xl md:text-3xl text-white/90 text-center" style={{ fontFamily: 'Cinzel, serif' }}>
              Goldnotes at TD
            </h2>
          </div>
          
          <div className="max-w-xl mx-auto">
            {tdProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      </section>

      {/* TD Benefits */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-xl text-white/90 mb-8 text-center" style={{ fontFamily: 'Cinzel, serif' }}>
              Why Buy from TD Precious Metals
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-6 bg-[#050505] border border-white/5 rounded-lg text-center">
                <div className="w-12 h-12 mx-auto mb-4 border border-[#34A853]/30 flex items-center justify-center rounded-lg">
                  <svg className="w-6 h-6 text-[#34A853]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h4 className="text-white/90 mb-2">TD Secure Storage</h4>
                <p className="text-xs text-white/70 font-light">
                  Store your precious metals in TD's secure vault facility without physical delivery.
                </p>
              </div>
              
              <div className="p-6 bg-[#050505] border border-white/5 rounded-lg text-center">
                <div className="w-12 h-12 mx-auto mb-4 border border-[#34A853]/30 flex items-center justify-center rounded-lg">
                  <svg className="w-6 h-6 text-[#34A853]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <h4 className="text-white/90 mb-2">Trusted Institution</h4>
                <p className="text-xs text-white/70 font-light">
                  Buy with confidence from one of Canada's largest financial institutions.
                </p>
              </div>
              
              <div className="p-6 bg-[#050505] border border-white/5 rounded-lg text-center">
                <div className="w-12 h-12 mx-auto mb-4 border border-[#34A853]/30 flex items-center justify-center rounded-lg">
                  <svg className="w-6 h-6 text-[#34A853]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h4 className="text-white/90 mb-2">Competitive Pricing</h4>
                <p className="text-xs text-white/70 font-light">
                  TD Customer Pricing with discounts when paying from your TD bank account.
                </p>
              </div>
            </div>
            
            <div className="mt-8 p-6 bg-[#34A853]/10 border border-[#34A853]/20 rounded-lg">
              <p className="text-sm text-white/70 text-center">
                <span className="text-[#34A853] font-medium">Note:</span> TD Precious Metals is available to TD Personal Banking Customers. 
                Free shipping on orders $500 CAD and over within Canada.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TDPreciousMetals;
